import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { appConfig } from '../../../config/appConfig';
import {
  buildOssEmbeddedCropProcess,
  readEmbeddedImageRecord,
  type EmbeddedImageData,
} from '../../ttqib/embeddedImageUtils';
import { assetService } from '../../../services/assetService';
import {
  CONTENT_FONT_SIZE_PT,
  CONTENT_LINE_HEIGHT_RATIO,
  CONTENT_FONT_FAMILY_CSS,
  buildContentDocumentFontLinksHtml,
} from '../../../theme/contentTypography';
import { buildAssetPreviewUrl } from '../../../utils/mediaUrl';
import { renderContentBodyHtml } from '../../../utils/renderRichTextHtml';
import { downloadHtml, presentSystemPrintFromHtml } from './presentSystemPrint';
import {
  REVISION_PRINT_ORIGIN_SPINE_CSS,
  buildRevisionPrintSpineRowMarkup,
  type RevisionPrintSourceOrigin,
} from './printParticleOrigin';
import { buildQrDataUrl } from '../../../lib/qrCode';

/** Print-time embedded crop record (Web: optional sourceAssetId). */
export type RevisionPrintEmbeddedImageRecord = EmbeddedImageData & {
  sourceAssetId?: string;
  displayWidth?: number;
  displayHeight?: number;
};

export type RevisionPrintParticle = {
  id: string;
  title?: string | null;
  questionMain: string;
  /** Bound subject spine path; empty when unbound. */
  spineLabel?: string | null;
  /** 阶梯练习 / 工厂滴灌 / 品题 — icon left of spine on each print item. */
  sourceOrigin?: RevisionPrintSourceOrigin | null;
  /** Raw metadata record — resolved to `embeddedImage` before preview/print. */
  embeddedImageRecord?: RevisionPrintEmbeddedImageRecord | null;
  embeddedImage?: RevisionPrintEmbeddedImage | null;
  answerMain?: string | null;
  fullAnswer?: string | null;
  subQuestions?: string[];
  subQuestionsExpanded: boolean;
  subCards?: Array<{
    question: string;
    answer?: string | null;
  }>;
  familySummary?: string | null;
};

export type { RevisionPrintSourceOrigin };

export type RevisionPrintCropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type RevisionPrintEmbeddedImage = {
  imageUrl: string;
  renderMode: 'oss-crop' | 'client-clip';
  cropRect: RevisionPrintCropRect;
  imageWidth: number;
  imageHeight: number;
  displayWidth?: number;
  displayHeight?: number;
};

const REVISION_PRINT_EMBED_MAX_WIDTH = 560;
const REVISION_PRINT_EMBED_MAX_HEIGHT = 300;
const REVISION_PRINT_EMBED_TARGET_PIXEL_RATIO = 2;

type UseRevisionPrintActionOptions = {
  particles: RevisionPrintParticle[];
  sessionId?: string | null;
  sheetKind?: PrintSheetKind;
  ttqaId?: string | null;
  onNotice?: (message: string) => void;
};

const KATEX_VERSION = '0.16.25';
const PARTICLES_PER_PAGE = 1;
/** A4 @ 96dpi — preview scales from this size to match print layout. */
export const REVISION_PRINT_A4_WIDTH_PX = 794;
export const REVISION_PRINT_A4_HEIGHT_PX = 1123;

function escapeHtml(input: string) {
  return String(input || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeImageSrcAttribute(url: string) {
  const normalized = String(url || '').trim();
  if (!normalized || normalized.startsWith('data:image/')) {
    return normalized;
  }
  return escapeHtml(normalized);
}

function detectMimeFromBase64(base64: string): string {
  const prefix = String(base64 || '').trim().slice(0, 8);
  if (prefix.startsWith('iVBOR')) {
    return 'image/png';
  }
  if (prefix.startsWith('/9j/')) {
    return 'image/jpeg';
  }
  if (prefix.startsWith('R0lG')) {
    return 'image/gif';
  }
  if (prefix.startsWith('UklGR')) {
    return 'image/webp';
  }
  return 'image/jpeg';
}

type EmbeddedCropLayoutInput = {
  cropRect: RevisionPrintCropRect;
  imageWidth: number;
  imageHeight: number;
  displayWidth?: number;
  displayHeight?: number;
};

export type EmbeddedCropMetrics = {
  containerWidth: number;
  containerHeight: number;
  renderedImageWidth: number;
  renderedImageHeight: number;
  offsetLeft: number;
  offsetTop: number;
};

/** Layout metrics for client-side overflow clipping (fallback path). */
export function getEmbeddedCropMetrics(
  record: EmbeddedCropLayoutInput,
  maxWidth: number,
  maxHeight: number,
): EmbeddedCropMetrics {
  const displayWidth = Math.max(1, record.displayWidth || record.cropRect.width);
  const displayHeight = Math.max(1, record.displayHeight || record.cropRect.height);
  const cropWidth = Math.max(1, record.cropRect.width);
  const cropHeight = Math.max(1, record.cropRect.height);
  const imageWidth = Math.max(1, record.imageWidth);
  const imageHeight = Math.max(1, record.imageHeight);
  const downscale = Math.min(maxWidth / displayWidth, maxHeight / displayHeight, 1);
  const containerWidth = Math.max(1, Math.round(displayWidth * downscale));
  const containerHeight = Math.max(1, Math.round(displayHeight * downscale));
  const scaleX = containerWidth / cropWidth;
  const scaleY = containerHeight / cropHeight;

  return {
    containerWidth,
    containerHeight,
    renderedImageWidth: Math.max(containerWidth, Math.round(imageWidth * scaleX)),
    renderedImageHeight: Math.max(containerHeight, Math.round(imageHeight * scaleY)),
    offsetLeft: -Math.round(record.cropRect.x * scaleX),
    offsetTop: -Math.round(record.cropRect.y * scaleY),
  };
}

function renderMathMarkup(input: string) {
  return renderContentBodyHtml(input);
}

/** Read print embed record from particle metadata (includes optional sourceAssetId). */
export function readRevisionPrintEmbeddedImageRecord(
  metadata: Record<string, unknown> | null | undefined,
): RevisionPrintEmbeddedImageRecord | null {
  const base = readEmbeddedImageRecord(metadata);
  if (!base) {
    return null;
  }
  const raw = (metadata?.['relatedGraphEmbeddedImage'] || metadata?.['embeddedImage']) as
    | Record<string, unknown>
    | null
    | undefined;
  const sourceAssetId = typeof raw?.['sourceAssetId'] === 'string' ? raw['sourceAssetId'].trim() : '';
  const displayWidth = Number(raw?.['displayWidth']);
  const displayHeight = Number(raw?.['displayHeight']);
  return {
    ...base,
    sourceAssetId: sourceAssetId || undefined,
    displayWidth: Number.isFinite(displayWidth) && displayWidth > 0 ? displayWidth : undefined,
    displayHeight: Number.isFinite(displayHeight) && displayHeight > 0 ? displayHeight : undefined,
  };
}

/** Extract OSS object key from URL (assets/... or ttq/...) or explicit key. */
export function extractOssKeyFromUrl(rawUrl?: string | null): string {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('assets/') || trimmed.startsWith('ttq/')) {
    return trimmed.replace(/^\/+/, '');
  }
  try {
    const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    const key = parsed.pathname.replace(/^\/+/, '').trim();
    if (key.startsWith('assets/') || key.startsWith('ttq/')) {
      return key;
    }
  } catch {
    // ignore
  }
  return '';
}

export function resolvePrintOssObjectKey(
  sourceAssetId?: string | null,
  sourceImageUrl?: string | null,
): string {
  const fromUrl = extractOssKeyFromUrl(sourceImageUrl);
  if (fromUrl) {
    return fromUrl;
  }
  const fromAsset = String(sourceAssetId || '').trim().replace(/^\/+/, '');
  if (fromAsset.startsWith('assets/') || fromAsset.startsWith('ttq/')) {
    return fromAsset;
  }
  return fromUrl;
}

function hasPrintEmbedSource(record: RevisionPrintEmbeddedImageRecord): boolean {
  return Boolean(resolvePrintOssObjectKey(record.sourceAssetId, record.sourceImageUrl)
    || String(record.sourceImageUrl || '').trim());
}

export function shortRevisionId(value?: string | null) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }

  if (normalized.includes('-')) {
    return normalized.split('-')[0] || normalized.slice(0, 8);
  }

  const parts = normalized.split('_').filter(Boolean);
  if (parts.length >= 2) {
    for (let index = parts.length - 1; index >= 0; index -= 1) {
      const segment = parts[index];
      if (/[0-9]/.test(segment) && segment.length >= 6) {
        return segment.slice(0, 8);
      }
      if (segment.length >= 8) {
        return segment.slice(0, 8);
      }
    }
  }

  return normalized.replace(/^[a-zA-Z]+_/, '').slice(0, 8);
}

function createPrintSessionId() {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `ps_${Date.now()}_${randomPart}`;
}

export type PrintSheetKind = 'pas' | 'ttqas';

/** Page-header sheet mark: PF-PAS-YYYYMMDD-<6 digits> — stable for one printSessionId. */
export function buildPasPrintSheetCode(printSessionId?: string | null, at: Date = new Date()) {
  const year = at.getFullYear();
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const day = String(at.getDate()).padStart(2, '0');
  const datePart = `${year}${month}${day}`;
  const seed = String(printSessionId || '');
  let numericSeed = 0;
  for (let i = 0; i < seed.length; i += 1) {
    numericSeed = (numericSeed * 31 + seed.charCodeAt(i)) >>> 0;
  }
  if (!seed) {
    numericSeed = Math.floor(Math.random() * 1_000_000);
  }
  const randomPart = String(numericSeed % 1_000_000).padStart(6, '0');
  return `PF-PAS-${datePart}-${randomPart}`;
}

/** Page-header sheet mark for TTQAS: PF-TTQ-YYYYMMDD-<6 digits>. */
export function buildTtqasPrintSheetCode(printSessionId?: string | null, at: Date = new Date()) {
  const year = at.getFullYear();
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const day = String(at.getDate()).padStart(2, '0');
  const datePart = `${year}${month}${day}`;
  const seed = String(printSessionId || '');
  let numericSeed = 0;
  for (let i = 0; i < seed.length; i += 1) {
    numericSeed = (numericSeed * 31 + seed.charCodeAt(i)) >>> 0;
  }
  if (!seed) {
    numericSeed = Math.floor(Math.random() * 1_000_000);
  }
  const randomPart = String(numericSeed % 1_000_000).padStart(6, '0');
  return `PF-TTQ-${datePart}-${randomPart}`;
}

export function buildPrintSheetCode(
  kind: PrintSheetKind,
  printSessionId?: string | null,
  at: Date = new Date(),
) {
  return kind === 'ttqas'
    ? buildTtqasPrintSheetCode(printSessionId, at)
    : buildPasPrintSheetCode(printSessionId, at);
}

export function resolvePrintSheetTitle(kind: PrintSheetKind) {
  return kind === 'ttqas' ? '品题答题纸' : '实战答题纸';
}

export function resolvePublicFrontendBase() {
  const explicit = String(
    import.meta.env.VITE_SITE_PUBLIC_URL
    || import.meta.env.VITE_WEBAPP_BASE_URL
    || import.meta.env.VITE_FRONTEND_URL
    || '',
  ).trim();
  if (explicit) {
    return explicit.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }

  try {
    const apiUrl = new URL(appConfig.apiBaseUrl);
    apiUrl.port = '5173';
    apiUrl.pathname = '';
    apiUrl.search = '';
    apiUrl.hash = '';
    return apiUrl.toString().replace(/\/+$/, '');
  } catch {
    return appConfig.apiBaseUrl.replace(/:\d+(?=\/|$)/, ':5173').replace(/\/+$/, '');
  }
}

export function buildParticleDetailUrl(particleId: string, baseUrl = resolvePublicFrontendBase()) {
  try {
    const url = new URL(`/particles/${encodeURIComponent(particleId)}`, baseUrl);
    return url.toString();
  } catch {
    return `${baseUrl.replace(/\/+$/, '')}/particles/${encodeURIComponent(particleId)}`;
  }
}

/** Compact PAS QR payload (pas_json_v1) — kpId + print session for scan dedupe. */
export function buildPasQrPayload(params: {
  particleId: string;
  printSessionId?: string | null;
}) {
  const kpId = String(params.particleId || '').trim();
  const sessionId = String(params.printSessionId || '').trim();
  const payload: Record<string, string | number> = {
    v: 1,
    kpId,
  };
  if (sessionId) {
    payload.sessionId = sessionId;
  }
  return JSON.stringify(payload);
}

/** TTQAS QR payload (ttqas_json_v1) — kpId + ttqaId + print session. */
export function buildTtqasQrPayload(params: {
  particleId: string;
  ttqaId: string;
  printSessionId?: string | null;
}) {
  const kpId = String(params.particleId || '').trim();
  const ttqaId = String(params.ttqaId || '').trim();
  const sessionId = String(params.printSessionId || '').trim();
  const payload: Record<string, string | number> = {
    v: 1,
    kind: 'ttqas',
    kpId,
    ttqaId,
  };
  if (sessionId) {
    payload.sessionId = sessionId;
  }
  return JSON.stringify(payload);
}

export function buildPrintQrPayload(
  kind: PrintSheetKind,
  params: {
    particleId: string;
    printSessionId?: string | null;
    ttqaId?: string | null;
  },
) {
  if (kind === 'ttqas') {
    return buildTtqasQrPayload({
      particleId: params.particleId,
      ttqaId: String(params.ttqaId || '').trim(),
      printSessionId: params.printSessionId,
    });
  }
  return buildPasQrPayload({
    particleId: params.particleId,
    printSessionId: params.printSessionId,
  });
}

/** Print QR bitmap size — keep sharp when CSS display is ~72px on A4. */
const PAS_PRINT_QR_BITMAP_PX = 180;

export function buildQrCodeUrl(value: string, size = PAS_PRINT_QR_BITMAP_PX) {
  return buildQrDataUrl({ value, size, margin: 2 });
}

function buildRevisionPrintEmbeddedImageFields(
  record: RevisionPrintEmbeddedImageRecord,
): Pick<RevisionPrintEmbeddedImage, 'cropRect' | 'imageWidth' | 'imageHeight' | 'displayWidth' | 'displayHeight'> {
  return {
    cropRect: {
      x: record.cropRect.x,
      y: record.cropRect.y,
      width: record.cropRect.width,
      height: record.cropRect.height,
    },
    imageWidth: record.imageWidth,
    imageHeight: record.imageHeight,
    displayWidth: record.displayWidth,
    displayHeight: record.displayHeight,
  };
}

function resolveRevisionPrintEmbeddedTargetPixelWidth(record: RevisionPrintEmbeddedImageRecord) {
  const metrics = getEmbeddedCropMetrics(
    record,
    REVISION_PRINT_EMBED_MAX_WIDTH,
    REVISION_PRINT_EMBED_MAX_HEIGHT,
  );
  return Math.max(
    64,
    Math.ceil(Math.max(metrics.containerWidth, metrics.containerHeight) * REVISION_PRINT_EMBED_TARGET_PIXEL_RATIO),
  );
}

function looksLikeInlinePrintImage(url?: string | null) {
  return String(url || '').trim().startsWith('data:image/');
}

/** Preview/print: allow data URLs and HTTPS signed OSS (browser can load). */
function looksLikePreviewPrintImage(url?: string | null) {
  const normalized = String(url || '').trim();
  if (!normalized) {
    return false;
  }
  if (looksLikeInlinePrintImage(normalized)) {
    return true;
  }
  return normalized.startsWith('https://') || normalized.startsWith('http://');
}

async function fetchUrlAsDataUrl(remoteUrl: string): Promise<string | null> {
  const normalized = String(remoteUrl || '').trim();
  if (!normalized) {
    return null;
  }
  if (looksLikeInlinePrintImage(normalized)) {
    return normalized;
  }
  try {
    const response = await fetch(normalized, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    const dataUrl = await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(typeof reader.result === 'string' && reader.result.startsWith('data:') ? reader.result : null);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
    if (dataUrl && dataUrl.length >= 100) {
      return dataUrl;
    }
    // Fallback: try base64 from ArrayBuffer if FileReader mime is odd.
    if (blob.size > 0 && !dataUrl) {
      const buffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      const mimeType = blob.type || detectMimeFromBase64(btoa(binary.slice(0, 16)));
      return `data:${mimeType};base64,${btoa(binary)}`;
    }
    return dataUrl;
  } catch {
    return null;
  }
}

async function resolveSignedEmbedUrl(
  objectKey: string,
  process?: string,
): Promise<string> {
  const signed = await assetService.getSignedOssUrl(objectKey, process ? { process } : undefined);
  const https = String(signed || '').trim().replace(/^http:\/\//i, 'https://');
  return buildAssetPreviewUrl(https) || https;
}

/**
 * Resolve OSS server-side crop URL for print/preview.
 * Prefer data:image inline (print-safe); on CORS failure keep HTTPS signed URL for preview.
 */
export async function resolveRevisionPrintEmbeddedImage(
  record: RevisionPrintEmbeddedImageRecord,
  _particleId: string,
  options?: { preferInline?: boolean },
): Promise<RevisionPrintEmbeddedImage | null> {
  if (!hasPrintEmbedSource(record)) {
    return null;
  }

  const objectKey = resolvePrintOssObjectKey(record.sourceAssetId, record.sourceImageUrl);
  const layout = buildRevisionPrintEmbeddedImageFields(record);
  const targetPixelWidth = resolveRevisionPrintEmbeddedTargetPixelWidth(record);
  const preferInline = options?.preferInline !== false;

  const finish = async (signedUrl: string, renderMode: RevisionPrintEmbeddedImage['renderMode']) => {
    const printableUrl = preferInline
      ? (await fetchUrlAsDataUrl(signedUrl)) || signedUrl
      : signedUrl;
    if (!looksLikePreviewPrintImage(printableUrl)) {
      return null;
    }
    return {
      ...layout,
      imageUrl: printableUrl,
      renderMode,
    } satisfies RevisionPrintEmbeddedImage;
  };

  if (objectKey) {
    const ossProcess = buildOssEmbeddedCropProcess(record, targetPixelWidth);
    try {
      const imageUrl = await resolveSignedEmbedUrl(objectKey, ossProcess);
      const resolved = await finish(imageUrl, 'oss-crop');
      if (resolved) {
        return resolved;
      }
    } catch {
      // fall through to full-image client-clip
    }

    try {
      const imageUrl = await resolveSignedEmbedUrl(objectKey);
      return await finish(imageUrl, 'client-clip');
    } catch {
      // fall through
    }
  }

  // Last resort: use source URL with client-side process query if present.
  const fallbackUrl = buildAssetPreviewUrl(record.sourceImageUrl) || String(record.sourceImageUrl || '').trim();
  if (!fallbackUrl) {
    return null;
  }
  return finish(fallbackUrl.replace(/^http:\/\//i, 'https://'), 'client-clip');
}

export async function resolveRevisionPrintParticlesEmbeddedImages(
  particles: RevisionPrintParticle[],
  options?: { preferInline?: boolean },
): Promise<RevisionPrintParticle[]> {
  return Promise.all(
    particles.map(async (particle) => {
      const record = particle.embeddedImageRecord ?? null;
      if (!record) {
        return particle.embeddedImage ? { ...particle, embeddedImage: null } : particle;
      }

      if (looksLikeInlinePrintImage(particle.embeddedImage?.imageUrl)) {
        return particle;
      }

      // Preview can keep HTTPS; print prefers re-inline.
      if (
        !options?.preferInline
        && looksLikePreviewPrintImage(particle.embeddedImage?.imageUrl)
      ) {
        return particle;
      }

      const embeddedImage = await resolveRevisionPrintEmbeddedImage(record, particle.id, options);
      return {
        ...particle,
        embeddedImage,
      };
    }),
  );
}

export function looksLikeRenderablePrintImage(url?: string | null) {
  const normalized = String(url || '').trim();
  if (!normalized) {
    return false;
  }

  return normalized.includes('/api/media/proxy?')
    || normalized.startsWith('data:image/')
    || normalized.startsWith('file://')
    || normalized.startsWith('https://')
    || normalized.startsWith('http://')
    || /\.(png|jpe?g|gif|webp|bmp)(\?.*)?$/i.test(normalized)
    || normalized.includes('/image/')
    || normalized.includes('image%2F');
}

export function buildPrintableImageUrl(rawUrl?: string | null) {
  const normalized = String(rawUrl || '').trim();
  if (!normalized) {
    return '';
  }

  // CHARTER: 禁止包 /api/media/proxy（双倍云出站）。打印预览用 HTTPS 直链 / 已签名 OSS。
  if (normalized.includes('/api/media/proxy?') || normalized.includes('/api/media/proxy/?')) {
    try {
      const parsed = new URL(
        normalized.startsWith('/')
          ? `${String(appConfig.apiBaseUrl || '').replace(/\/+$/, '')}${normalized}`
          : normalized,
      );
      const nested = String(parsed.searchParams.get('url') || '').trim();
      if (nested) {
        return buildPrintableImageUrl(nested);
      }
    } catch {
      // fall through
    }
  }

  if (
    normalized.startsWith('data:image/')
    || normalized.startsWith('file://')
  ) {
    return normalized;
  }

  try {
    const parsed = new URL(normalized.startsWith('//') ? `https:${normalized}` : normalized);
    if (parsed.protocol === 'http:') {
      return `https://${parsed.host}${parsed.pathname}${parsed.search}`;
    }
    return parsed.toString();
  } catch {
    return normalized.startsWith('/')
      ? `${String(appConfig.apiBaseUrl || '').replace(/\/+$/, '')}${normalized}`
      : normalized;
  }
}

export function getRevisionPrintEmbeddedImageMetrics(
  image: RevisionPrintEmbeddedImage,
  maxWidth: number,
  maxHeight: number,
) {
  return getEmbeddedCropMetrics(image, maxWidth, maxHeight);
}

function buildRevisionPrintEmbeddedImagePlaceholder(record: RevisionPrintEmbeddedImageRecord) {
  const metrics = getEmbeddedCropMetrics(
    record,
    REVISION_PRINT_EMBED_MAX_WIDTH,
    REVISION_PRINT_EMBED_MAX_HEIGHT,
  );

  return `
    <div
      class="embedded-image-frame embedded-image-native-slot"
      style="width:${metrics.containerWidth}px;height:${metrics.containerHeight}px;"
    ></div>
  `;
}

function buildRevisionPrintEmbeddedImageMarkup(image: RevisionPrintEmbeddedImage) {
  const metrics = getRevisionPrintEmbeddedImageMetrics(
    image,
    REVISION_PRINT_EMBED_MAX_WIDTH,
    REVISION_PRINT_EMBED_MAX_HEIGHT,
  );

  if (image.renderMode === 'oss-crop') {
    return `
    <div class="embedded-image-frame" style="width:${metrics.containerWidth}px;height:${metrics.containerHeight}px;">
      <img
        class="embedded-image-canvas embedded-image-cropped"
        src="${escapeImageSrcAttribute(image.imageUrl)}"
        alt="知识颗粒嵌入图片"
        style="width:100%;height:100%;object-fit:fill;"
      />
    </div>
  `;
  }

  return `
    <div class="embedded-image-frame" style="width:${metrics.containerWidth}px;height:${metrics.containerHeight}px;">
      <img
        class="embedded-image-canvas"
        src="${escapeImageSrcAttribute(image.imageUrl)}"
        alt="知识颗粒嵌入图片"
        style="width:${metrics.renderedImageWidth}px;height:${metrics.renderedImageHeight}px;left:${metrics.offsetLeft}px;top:${metrics.offsetTop}px;"
      />
    </div>
  `;
}

function resolveRevisionPrintSubCards(particle: RevisionPrintParticle) {
  return (particle.subCards && particle.subCards.length > 0
    ? particle.subCards
    : (particle.subQuestions || []).map((question) => ({ question, answer: '' })))
    .filter((item) => item.question.trim() || String(item.answer || '').trim());
}

export function filterPrintableRevisionParticles(particles: RevisionPrintParticle[]) {
  // Always include particles: 0-sub-question cards still print the main stem.
  // subQuestionsExpanded only controls whether sub-questions are shown, not particle inclusion.
  return particles.slice();
}

export function chunkRevisionPrintParticles(particles: RevisionPrintParticle[]) {
  const pages: RevisionPrintParticle[][] = [];
  for (let index = 0; index < particles.length; index += PARTICLES_PER_PAGE) {
    pages.push(particles.slice(index, index + PARTICLES_PER_PAGE));
  }
  return pages;
}

export function resolveRevisionPrintPreviewScale(viewportWidth: number) {
  return Math.min(1, Math.max(0.2, (viewportWidth - 32) / REVISION_PRINT_A4_WIDTH_PX));
}

export function buildRevisionPrintHtml(params: {
  particles: RevisionPrintParticle[];
  sessionId?: string | null;
  printSessionId: string;
  /** pas = 实战答题纸 (default); ttqas = 品题答题纸 */
  sheetKind?: PrintSheetKind;
  /** Required when sheetKind=ttqas — embedded in QR for scan-back to TTQ canvas. */
  ttqaId?: string | null;
  /** When set, wraps pages in a scaled A4 preview shell for WebView display. */
  previewViewportWidth?: number;
  /** Preview only: reserve layout slot; native EmbeddedCropImage renders the pixels. */
  omitEmbeddedImageSrc?: boolean;
  /** Tight preview shell for per-page WebView + native image overlay. */
  embeddedImageOverlayPreview?: boolean;
}) {
  const sheetKind: PrintSheetKind = params.sheetKind === 'ttqas' ? 'ttqas' : 'pas';
  const printableParticles = filterPrintableRevisionParticles(params.particles);
  const pages = chunkRevisionPrintParticles(printableParticles);
  const now = new Date();
  const printedAt = now.toLocaleDateString('zh-CN');
  const sheetCode = buildPrintSheetCode(sheetKind, params.printSessionId, now);
  const sheetTitle = resolvePrintSheetTitle(sheetKind);
  const previewScale = params.previewViewportWidth
    ? resolveRevisionPrintPreviewScale(params.previewViewportWidth)
    : null;
  const overlayPreview = Boolean(params.embeddedImageOverlayPreview && previewScale);
  const previewFrameWidth = previewScale
    ? Math.round(REVISION_PRINT_A4_WIDTH_PX * previewScale)
    : null;
  const previewFrameHeight = previewScale
    ? Math.round(REVISION_PRINT_A4_HEIGHT_PX * previewScale)
    : null;

  const pagesMarkup = pages.map((page, pageIndex) => {
    const particlesMarkup = page.map((particle, particleIndex) => {
      const qrPayload = buildPrintQrPayload(sheetKind, {
        particleId: particle.id,
        printSessionId: params.printSessionId,
        ttqaId: params.ttqaId,
      });
      const qrCodeUrl = buildQrCodeUrl(qrPayload);
      const sessionShortId = shortRevisionId(params.sessionId);
      const printShortId = shortRevisionId(params.printSessionId);
      const headerMeta = [
        sessionShortId ? `SID ${sessionShortId}` : '',
        printShortId ? `PID ${printShortId}` : '',
      ].filter(Boolean).join(' ');
      const subCards = resolveRevisionPrintSubCards(particle);
      const subQuestionsExpanded = particle.subQuestionsExpanded !== false;
      const embeddedImage = particle.embeddedImage && looksLikePreviewPrintImage(particle.embeddedImage.imageUrl)
        ? particle.embeddedImage
        : null;
      const embeddedImageMarkup = params.omitEmbeddedImageSrc && particle.embeddedImageRecord
        ? buildRevisionPrintEmbeddedImagePlaceholder(particle.embeddedImageRecord)
        : embeddedImage
          ? buildRevisionPrintEmbeddedImageMarkup(embeddedImage)
          : '';

      return `
        <section class="particle-card">
          <div class="particle-content">
            <div class="particle-header">
              <div class="particle-header-main">
                <div class="particle-index">${pageIndex * PARTICLES_PER_PAGE + particleIndex + 1}</div>
                <div class="particle-header-copy">
                  <span class="particle-header-meta">ID ${shortRevisionId(particle.id)}</span>
                  <span class="particle-header-session">${escapeHtml(headerMeta || '扫码查看详情')}</span>
                </div>
              </div>
              <img class="particle-qr-header" src="${escapeHtml(qrCodeUrl)}" alt="知识颗粒二维码" />
            </div>
            ${buildRevisionPrintSpineRowMarkup({
              sourceOrigin: particle.sourceOrigin,
              spineLabel: particle.spineLabel,
              escapeHtml,
            })}
            <div class="question-main">${renderMathMarkup(particle.questionMain || particle.title || '未写入题面')}</div>
            ${embeddedImageMarkup}
            ${subCards.length > 0 ? (
              subQuestionsExpanded
                ? `
                  <section class="sub-card-list">
                    ${subCards.map((subCard, subIndex) => `
                      <article class="sub-card-row">
                        <div class="sub-card-index">${subIndex + 1}.</div>
                        <div class="sub-card-question">${renderMathMarkup(subCard.question || '未写入子问题')}</div>
                      </article>
                    `).join('')}
                  </section>
                `
                : ''
            ) : ''}
          </div>
        </section>
      `;
    }).join('');

    const pageMarkup = `
      <article class="print-page">
        <header class="page-header">
          <div class="page-header-brand">
            <span class="page-header-title">${escapeHtml(sheetTitle)}</span>
            <span class="page-header-code">${escapeHtml(sheetCode)}</span>
            <span class="page-header-page">第 ${pageIndex + 1} 页</span>
          </div>
          <span class="page-header-time">${printedAt}</span>
        </header>
        <main class="page-body">${particlesMarkup}</main>
      </article>
    `;

    if (previewScale && previewFrameWidth && previewFrameHeight) {
      return `
        <div class="preview-page-frame" style="width:${previewFrameWidth}px;height:${previewFrameHeight}px;">
          <div class="preview-page-inner" style="transform:scale(${previewScale});">
            ${pageMarkup}
          </div>
        </div>
      `;
    }

    return pageMarkup;
  }).join('');

  const bodyMarkup = previewScale
    ? `<div class="preview-root">${pagesMarkup}</div>`
    : pagesMarkup;

  const previewStyles = previewScale
    ? `
      body.preview-mode {
        background: #eef2f6;
        height: auto;
        min-height: 100%;
        padding: ${overlayPreview ? '0' : '16px 0 24px'};
      }
      .preview-root {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${overlayPreview ? '0' : '16px'};
      }
      .preview-page-frame {
        overflow: hidden;
        border-radius: 8px;
        background: #ffffff;
        box-shadow: 0 8px 28px rgba(15, 23, 42, 0.12);
        flex: none;
      }
      .preview-page-inner {
        width: ${REVISION_PRINT_A4_WIDTH_PX}px;
        height: ${REVISION_PRINT_A4_HEIGHT_PX}px;
        transform-origin: top left;
      }
      body.preview-mode .print-page {
        width: ${REVISION_PRINT_A4_WIDTH_PX}px;
        height: ${REVISION_PRINT_A4_HEIGHT_PX}px;
        page-break-after: auto;
        break-after: auto;
      }
    `
    : '';

  return `<!DOCTYPE html>
<html lang="zh-CN">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    ${buildContentDocumentFontLinksHtml()}
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/katex.min.css" />
    <style>
      @page { size: A4 portrait; margin: 0; }
      * { box-sizing: border-box; }
      html, body, article, header, main, section, footer, aside, figure {
        margin: 0;
        padding: 0;
      }
      html, body {
        height: 100%;
      }
      body {
        color: #0f172a;
        background: #ffffff;
        font-family: ${CONTENT_FONT_FAMILY_CSS};
        font-size: ${CONTENT_FONT_SIZE_PT}pt;
        line-height: ${CONTENT_LINE_HEIGHT_RATIO};
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        width: 100%;
      }
      .print-page {
        padding: 10mm 16mm;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .print-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }
      .page-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 16px;
        margin-bottom: 8px;
        padding-bottom: 6px;
        border-bottom: 1px solid #cbd5e1;
      }
      .page-header-brand {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 8px;
        min-width: 0;
      }
      .page-header-title {
        color: #0f172a;
        font-size: 13px;
        font-weight: 800;
        letter-spacing: 0.02em;
      }
      .page-header-code {
        color: #1d4ed8;
        font-size: 11px;
        font-weight: 800;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        letter-spacing: 0.04em;
      }
      .page-header-page,
      .page-header-time {
        color: #64748b;
        font-size: 11px;
        font-weight: 600;
      }
      .page-body {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }
      .particle-card {
        padding: 4px 0 0;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .particle-content {
        display: flex;
        flex-direction: column;
        gap: 7px;
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }
      .particle-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
        flex: none;
      }
      .particle-header-main {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        flex: 1;
        min-width: 0;
      }
      .particle-index {
        width: 18px;
        height: 18px;
        border-radius: 999px;
        background: #eff6ff;
        color: #1d4ed8;
        font-size: 10px;
        font-weight: 800;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
      }
      .particle-header-copy {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .particle-header-meta {
        color: #334155;
        font-size: 7px;
        line-height: 10px;
        font-weight: 800;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .particle-header-session {
        color: #94a3b8;
        font-size: 6px;
        line-height: 8px;
      }
      ${REVISION_PRINT_ORIGIN_SPINE_CSS}
      .particle-qr-header {
        width: 72px;
        height: 72px;
        border-radius: 8px;
        background: #ffffff;
        flex: none;
        image-rendering: pixelated;
      }
      .question-main {
        color: #0f172a;
        font-weight: 800;
        font-size: ${CONTENT_FONT_SIZE_PT}pt;
        line-height: ${CONTENT_LINE_HEIGHT_RATIO};
      }
      .embedded-image-frame {
        position: relative;
        overflow: hidden;
        border-radius: 8px;
        background: #f8fafc;
        flex: none;
        max-width: 100%;
      }
      .embedded-image-canvas {
        position: absolute;
        display: block;
        max-width: none;
      }
      .embedded-image-cropped {
        position: static;
        display: block;
        width: 100%;
        height: 100%;
        max-width: none;
      }
      .sub-card-list {
        display: grid;
        gap: 5px;
        min-height: 0;
        overflow: hidden;
      }
      .sub-card-status {
        color: #64748b;
        font-size: 9px;
        line-height: 12px;
        font-weight: 600;
        padding-left: 20px;
      }
      .sub-card-row {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        padding: 0;
      }
      .sub-card-index {
        width: 14px;
        color: #64748b;
        font-size: ${CONTENT_FONT_SIZE_PT}pt;
        line-height: ${CONTENT_LINE_HEIGHT_RATIO};
        font-weight: 800;
        flex: none;
      }
      .sub-card-question {
        flex: 1;
        min-width: 0;
        color: #334155;
        font-size: ${CONTENT_FONT_SIZE_PT}pt;
        line-height: ${CONTENT_LINE_HEIGHT_RATIO};
      }
      .question-main > :first-child,
      .sub-card-question > :first-child {
        margin-top: 0;
      }
      .question-main > :last-child,
      .sub-card-question > :last-child {
        margin-bottom: 0;
      }
      .question-main p,
      .sub-card-question p,
      .question-main ul,
      .sub-card-question ul,
      .question-main ol,
      .sub-card-question ol,
      .question-main blockquote,
      .sub-card-question blockquote,
      .question-main pre,
      .sub-card-question pre,
      .question-main table,
      .sub-card-question table,
      .question-main hr,
      .sub-card-question hr {
        margin: 0.18em 0;
      }
      .question-main ul,
      .sub-card-question ul,
      .question-main ol,
      .sub-card-question ol {
        padding-left: 1.2em;
      }
      .question-main li,
      .sub-card-question li {
        margin: 0.08em 0;
      }
      .question-main h1,
      .sub-card-question h1,
      .question-main h2,
      .sub-card-question h2,
      .question-main h3,
      .sub-card-question h3,
      .question-main h4,
      .sub-card-question h4,
      .question-main h5,
      .sub-card-question h5,
      .question-main h6,
      .sub-card-question h6 {
        margin: 0.22em 0 0.12em;
        line-height: 1.25;
      }
      .question-main h1,
      .sub-card-question h1 {
        font-size: 1.16em;
      }
      .question-main h2,
      .sub-card-question h2 {
        font-size: 1.12em;
      }
      .question-main h3,
      .sub-card-question h3,
      .question-main h4,
      .sub-card-question h4,
      .question-main h5,
      .sub-card-question h5,
      .question-main h6,
      .sub-card-question h6 {
        font-size: 1.05em;
      }
      .question-main blockquote,
      .sub-card-question blockquote {
        padding-left: 0.72em;
        border-left: 2px solid #cbd5e1;
        color: #475569;
      }
      .question-main pre,
      .sub-card-question pre {
        padding: 0.35em 0.48em;
        border-radius: 6px;
        background: rgba(15, 23, 42, 0.05);
        white-space: pre-wrap;
      }
      .question-main table,
      .sub-card-question table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.94em;
      }
      .question-main th,
      .sub-card-question th,
      .question-main td,
      .sub-card-question td {
        border: 1px solid #e2e8f0;
        padding: 0.18em 0.32em;
        text-align: left;
        vertical-align: top;
      }
      .question-main hr,
      .sub-card-question hr {
        border: 0;
        border-top: 1px solid #e2e8f0;
      }
      .question-main img,
      .sub-card-question img {
        max-width: 100%;
        height: auto;
      }
      .question-main,
      .sub-card-question,
      .question-main .text-run,
      .sub-card-question .text-run,
      .particle-content,
      .sub-card-index {
        font-family: ${CONTENT_FONT_FAMILY_CSS};
      }
      strong {
        font-weight: 800;
      }
      em {
        font-style: italic;
      }
      del {
        text-decoration: line-through;
      }
      code {
        font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
        font-size: 0.92em;
        background: rgba(15, 23, 42, 0.06);
        border-radius: 4px;
        padding: 0.08em 0.24em;
      }
      a {
        color: #1d4ed8;
        text-decoration: none;
      }
      .text-run { white-space: pre-wrap; }
      .math-display { margin: 0.3em 0; max-width: 100%; }
      .katex { font-size: 1.02em; max-width: 100%; }
      .katex-display {
        display: block;
        text-align: center;
        margin: 0.45em 0;
        max-width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
      }
      .katex-display > .katex {
        text-align: center;
        white-space: nowrap;
      }
      ${previewStyles}
    </style>
  </head>
  <body class="${previewScale ? 'preview-mode' : ''}">${bodyMarkup}</body>
</html>`;
}

export function useRevisionPrintAction({
  particles,
  sessionId,
  sheetKind = 'pas',
  ttqaId = null,
  onNotice,
}: UseRevisionPrintActionOptions) {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [resolvedParticles, setResolvedParticles] = useState(particles);
  const [resolvingEmbeddedImages, setResolvingEmbeddedImages] = useState(false);
  const printSessionId = useMemo(createPrintSessionId, []);
  const printJobGenerationRef = useRef(0);
  const particlesRef = useRef(particles);
  particlesRef.current = particles;

  // Keep preview particles in sync on the same render as `particles` updates,
  // so openPreview does not flash an empty "暂无可预览内容" state.
  const [particlesSyncKey, setParticlesSyncKey] = useState(particles);
  if (particles !== particlesSyncKey) {
    setParticlesSyncKey(particles);
    setResolvedParticles(particles);
  }

  useEffect(() => {
    let cancelled = false;
    if (!particles.length) {
      setResolvingEmbeddedImages(false);
      return () => {
        cancelled = true;
      };
    }
    const needsEmbed = particles.some((particle) => Boolean(particle.embeddedImageRecord));
    if (!needsEmbed) {
      setResolvingEmbeddedImages(false);
      return () => {
        cancelled = true;
      };
    }
    setResolvingEmbeddedImages(true);
    void resolveRevisionPrintParticlesEmbeddedImages(particles, { preferInline: false }).then((next) => {
      if (!cancelled) {
        setResolvedParticles(next);
        setResolvingEmbeddedImages(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [particles]);

  const printableParticles = useMemo(
    () => filterPrintableRevisionParticles(resolvedParticles),
    [resolvedParticles],
  );

  const buildPrintHtml = useCallback(async () => {
    // Prefer data:image inline for print; HTTPS signed URLs remain as CORS fallback.
    const withInlineImages = await resolveRevisionPrintParticlesEmbeddedImages(
      printableParticles.map((particle) => ({ ...particle, embeddedImage: null })),
      { preferInline: true },
    );
    setResolvedParticles(withInlineImages);
    return buildRevisionPrintHtml({
      particles: filterPrintableRevisionParticles(withInlineImages),
      sessionId,
      printSessionId,
      sheetKind,
      ttqaId,
    });
  }, [printableParticles, printSessionId, sessionId, sheetKind, ttqaId]);

  const openPreview = useCallback(() => {
    if (!particlesRef.current.length) {
      onNotice?.(sheetKind === 'ttqas' ? '暂无可打印的品题颗粒。' : '暂无可打印的答题纸题目。');
      return;
    }

    setPreviewVisible(true);
  }, [onNotice, sheetKind]);

  const closePreview = useCallback(() => {
    printJobGenerationRef.current += 1;
    setPrinting(false);
    setSharing(false);
    setPreviewVisible(false);
  }, []);

  const print = useCallback(async () => {
    if (resolvingEmbeddedImages) {
      onNotice?.('嵌入图片正在准备中，请稍候。');
      return;
    }

    if (!printableParticles.length) {
      onNotice?.('请至少选择一个参与打印的知识颗粒。');
      return;
    }

    const jobId = printJobGenerationRef.current + 1;
    printJobGenerationRef.current = jobId;
    setPrinting(true);

    try {
      const html = await buildPrintHtml();
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      const outcome = await presentSystemPrintFromHtml({
        html,
        shareFallbackDialogTitle: '分享复习打印 PDF',
        hidePreview: () => setPreviewVisible(false),
        isAborted: () => jobId !== printJobGenerationRef.current,
      });
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      if (outcome === 'printed') {
        onNotice?.('已打开系统打印面板。');
      } else if (outcome === 'shared-print-service') {
        onNotice?.('请选择卓易通或其他应用完成打印。');
      } else {
        onNotice?.('系统打印不可用，已生成打印稿下载。');
      }
    } catch (printError) {
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      const message = printError instanceof Error ? printError.message : '无法生成复习打印材料';
      onNotice?.(`打印失败：${message}`);
      window.alert(`打印失败\n${message}`);
    } finally {
      if (jobId === printJobGenerationRef.current) {
        setPrinting(false);
      }
    }
  }, [buildPrintHtml, onNotice, printableParticles.length, resolvingEmbeddedImages]);

  const share = useCallback(async () => {
    if (resolvingEmbeddedImages) {
      onNotice?.('嵌入图片正在准备中，请稍候。');
      return;
    }

    if (!printableParticles.length) {
      onNotice?.('请至少选择一个参与打印的知识颗粒。');
      return;
    }

    const jobId = printJobGenerationRef.current + 1;
    printJobGenerationRef.current = jobId;
    setSharing(true);
    try {
      const html = await buildPrintHtml();
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      const sheetTitle = resolvePrintSheetTitle(sheetKind);
      downloadHtml(html, `${sheetTitle}-${shortRevisionId(printSessionId) || 'print'}.html`);
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      onNotice?.('已下载打印稿（HTML）。可在浏览器中打开后「打印 → 存储为 PDF」。');
    } catch (shareError) {
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      const message = shareError instanceof Error ? shareError.message : '无法生成复习打印材料';
      onNotice?.(`生成 PDF 失败：${message}`);
      window.alert(`生成 PDF 失败\n${message}`);
    } finally {
      if (jobId === printJobGenerationRef.current) {
        setSharing(false);
      }
    }
  }, [buildPrintHtml, onNotice, printableParticles.length, printSessionId, resolvingEmbeddedImages, sheetKind]);

  return {
    openPreview,
    closePreview,
    previewVisible,
    print,
    share,
    printing,
    sharing,
    printSessionId,
    resolvedParticles,
    resolvingEmbeddedImages,
  };
}